import { useState } from 'react';
import { Users, Plus, Trash2, User, Shield, Loader2, Building2, X } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { EmployeeSelector } from '@/components/EmployeeSelector';
import {
  useClientAccess,
  useAvailableRecipients,
  useCreateDelegation,
  useDeleteDelegation,
} from './hooks/useClientAccess';
import type { AccessListItem } from '@/api/delegations';

interface ClientAccessSheetProps {
  clientId: number | undefined;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const ROLE_LABELS: Record<string, string> = {
  admin: 'Администратор',
  accountant: 'Бухгалтер',
  manager: 'Менеджер',
};

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('ru', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function AccessGroup({
  title,
  items,
  canDelete,
  onDelete,
}: {
  title: string;
  items: AccessListItem[];
  canDelete?: boolean;
  onDelete?: (item: AccessListItem) => void;
}) {
  if (items.length === 0) return null;

  return (
    <div className="space-y-2">
      <h4 className="text-sm font-medium text-muted-foreground">{title}</h4>
      <div className="space-y-1">
        {items.map((item, idx) => (
          <div
            key={idx}
            className="flex items-center justify-between p-2 bg-muted/50 rounded-lg"
          >
            <div className="flex items-center gap-2">
              {item.type === 'role' ? (
                <Shield className="h-4 w-4 text-muted-foreground" />
              ) : item.type === 'additionalOffice' ? (
                <Building2 className="h-4 w-4 text-muted-foreground" />
              ) : (
                <User className="h-4 w-4 text-muted-foreground" />
              )}
              <span className="text-sm">
                {item.type === 'role' && item.role
                  ? ROLE_LABELS[item.role] || item.role
                  : item.employee
                    ? `${item.employee.lastName} ${item.employee.firstName}`
                    : '—'}
              </span>
              {item.expiresAt && (
                <Badge variant="outline" className="text-xs">
                  до {formatDate(item.expiresAt)}
                </Badge>
              )}
              {item.via && (
                <span className="text-xs text-muted-foreground">
                  ({item.via})
                </span>
              )}
            </div>
            {canDelete && item.type === 'delegated' && onDelete && (
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                onClick={() => onDelete(item)}
              >
                <Trash2 className="h-3 w-3" />
              </Button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function AddDelegationForm({
  clientId,
  onSuccess,
}: {
  clientId: number;
  onSuccess: () => void;
}) {
  // Максимальная дата - 3 месяца от сегодня (по умолчанию)
  const maxDate = new Date();
  maxDate.setMonth(maxDate.getMonth() + 3);
  const maxDateStr = maxDate.toISOString().split('T')[0];

  // Минимальная дата - завтра
  const minDate = new Date();
  minDate.setDate(minDate.getDate() + 1);
  const minDateStr = minDate.toISOString().split('T')[0];

  const [toEmployeeId, setToEmployeeId] = useState('');
  const [expiresAt, setExpiresAt] = useState(maxDateStr);

  const { data: recipients, isLoading: loadingRecipients } = useAvailableRecipients();
  const createDelegation = useCreateDelegation();

  const handleSubmit = () => {
    if (!toEmployeeId || !expiresAt) return;

    createDelegation.mutate(
      { clientId, toEmployeeId, expiresAt },
      { onSuccess }
    );
  };

  return (
    <div className="space-y-4 p-4 border rounded-lg bg-muted/30">
      <h4 className="font-medium">Делегировать доступ</h4>

      <div className="space-y-2">
        <Label>Сотрудник</Label>
        <EmployeeSelector
          value={toEmployeeId}
          onChange={setToEmployeeId}
          employees={recipients}
          isLoading={loadingRecipients}
        />
      </div>

      <div className="space-y-2">
        <Label>Срок действия</Label>
        <Input
          type="date"
          value={expiresAt}
          onChange={(e) => setExpiresAt(e.target.value)}
          min={minDateStr}
          max={maxDateStr}
        />
        <p className="text-xs text-muted-foreground">
          Максимум 3 месяца от сегодня
        </p>
      </div>

      <Button
        className="w-full"
        onClick={handleSubmit}
        disabled={!toEmployeeId || !expiresAt || createDelegation.isPending}
      >
        {createDelegation.isPending ? (
          <Loader2 className="h-4 w-4 animate-spin mr-2" />
        ) : (
          <Plus className="h-4 w-4 mr-2" />
        )}
        Делегировать
      </Button>
    </div>
  );
}

export function ClientAccessSheet({
  clientId,
  open,
  onOpenChange,
}: ClientAccessSheetProps) {
  const [showAddForm, setShowAddForm] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<AccessListItem | null>(null);

  const { data, isLoading } = useClientAccess(clientId);
  const deleteDelegation = useDeleteDelegation();

  // Группируем по типу
  const roleAccess = data?.accessList.filter((a) => a.type === 'role') || [];
  const ownAccess = data?.accessList.filter((a) => a.type === 'own') || [];
  const delegatedAccess = data?.accessList.filter((a) => a.type === 'delegated') || [];
  const subordinateAccess = data?.accessList.filter((a) => a.type === 'subordinate') || [];
  const additionalOfficeAccess = data?.accessList.filter((a) => a.type === 'additionalOffice') || [];

  const handleDeleteClick = (item: AccessListItem) => {
    setItemToDelete(item);
  };

  const handleDeleteConfirm = () => {
    if (itemToDelete?.delegationId) {
      deleteDelegation.mutate(itemToDelete.delegationId, {
        onSuccess: () => setItemToDelete(null),
      });
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="max-h-[85vh] overflow-y-auto" hideCloseButton>
        <SheetHeader>
          <div className="flex items-center justify-between">
            <SheetTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Кто видит клиента
            </SheetTitle>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={(e) => {
                e.stopPropagation();
                onOpenChange(false);
              }}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </SheetHeader>

        {isLoading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="space-y-4 mt-4">
            <AccessGroup title="Ответственный" items={ownAccess} />
            <AccessGroup title="Через доп.офисы" items={additionalOfficeAccess} />
            <AccessGroup
              title="Делегировано"
              items={delegatedAccess}
              canDelete={data?.canManage}
              onDelete={handleDeleteClick}
            />
            <AccessGroup title="Через подчинённых" items={subordinateAccess} />
            <AccessGroup title="Полный доступ (роль)" items={roleAccess} />

            {/* Кнопка/форма добавления делегирования */}
            {data?.canManage && clientId && (
              <>
                {showAddForm ? (
                  <AddDelegationForm
                    clientId={clientId}
                    onSuccess={() => setShowAddForm(false)}
                  />
                ) : (
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => setShowAddForm(true)}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Делегировать доступ
                  </Button>
                )}
              </>
            )}
          </div>
        )}
      </SheetContent>

      {/* Подтверждение удаления делегирования */}
      <AlertDialog open={!!itemToDelete} onOpenChange={(open) => !open && setItemToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Удалить делегирование?</AlertDialogTitle>
            <AlertDialogDescription>
              {itemToDelete?.employee && (
                <>
                  Доступ для {itemToDelete.employee.lastName} {itemToDelete.employee.firstName} будет отозван.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Отмена</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              disabled={deleteDelegation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteDelegation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Trash2 className="h-4 w-4 mr-2" />
              )}
              Удалить
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Sheet>
  );
}
